import * as Api from '@ViewModels';
import { asApiError, AttachmentsViewModel, EmailMessageViewModel, IFileAttachment } from '@ViewModels';
import { IGif } from '@giphy/js-types';
import { css, StyleDeclarationValue } from 'aphrodite';
import { inject } from 'mobx-react';
import * as React from 'react';
import * as tinymce from 'tinymce';
import { v4 as uuid } from 'uuid';
import {
	disableFileAttachmentCommand,
	enableFileAttachmentCommand,
	IImpersonationContextComponentProps,
	ImpersonationContextKey,
	IRichContentDocumentEditorImageOptions,
	IRichContentDocumentEditorPlaceholder,
	levEditImageCommand,
	levInsertGiphyCommand,
	levShowImageChooserCommand,
	levShowPixabayChooserCommand,
	levShowYouTubeChooserCommand,
} from '../../../../models';
import { IUserSessionComponentProps, UserSessionViewModelKey } from '../../../../models/AppState';
import { isIE11 } from '../../../../models/Browser';
import { FirstNamePlaceholder, GetDefaultEmailPlaceholders, Token } from '../../../../models/Token';
import { createContentStateWithHtmlStringValue, ToolbarSignature } from '../../../../models/UiUtils';
import { IYouTubeVideoInfo, YouTubeUtils } from '../../../../models/YouTubeUtils';
import { useContactCustomFields, useContactIntegrationCustomFieldsQuery } from '../../../../queries';
import {
	getContentHtmlStringValueFromRawRichTextContentState,
	IOperationResultNoValue,
	IRichContentEditorState,
	IRichContentEditorStateAttributes,
} from '../../../../viewmodels/AppViewModels';
import { UnifiedImageDialog } from '../../../components-v2/UnifiedImageDialog';
import { LoadingSpinner } from '../../LoadingSpinner';
import { MediaChooserModal } from '../../MediaChooser';
import { GiphyChooserModal } from '../GiphyChooser';
import { YouTubeChooserModal } from '../YouTubeChooser';
import { PlaceholdersPlugin } from './plugins';
import { styleSheet } from './styles';
import './styles.less';

const LazyHtmlEditor = React.lazy(() => import('./HtmlEditor'));

// Convert image element to IFileAttachment for editing
const convertImageElementToFileAttachment = async (
	imgElement: HTMLImageElement,
	userSession: Api.UserSessionContext,
	impersonationContext?: Api.IImpersonationContext
): Promise<IFileAttachment> => {
	try {
		// Check if image already has a data-id attribute (existing Levitate attachment)
		const existingId = imgElement.getAttribute('data-id');
		if (existingId) {
			// Image is already a Levitate attachment, reconstruct IFileAttachment from element
			const existingFilename = imgElement.getAttribute('alt') || imgElement.src.split('/').pop() || 'image.jpg';

			return {
				id: existingId,
				url: imgElement.src,
				fileName: existingFilename,
				fileSize: 0, // Size not available from DOM, but not needed for editing
				mimeType: 'image/jpeg', // Assume JPEG, actual type doesn't matter for editing
			};
		}

		// Image is external or newly inserted, need to fetch and upload
		const response = await fetch(imgElement.src);
		const blob = await response.blob();

		// Create file from blob
		const filename = imgElement.src.split('/').pop() || 'image.jpg';
		const file = new File([blob], filename, { type: blob.type });

		// Upload to get IFileAttachment
		const attachments = new AttachmentsViewModel(userSession).impersonate(impersonationContext);
		const opResult = await attachments.uploadImages([file]);

		if (opResult.succeeded?.length !== 1) {
			throw new Error('Failed to upload image');
		}

		return opResult.succeeded[0];
	} catch (error) {
		console.error('Failed to convert image element to file attachment:', error);
		throw error;
	}
};

/**
 * TinyMCE font formats configuration for email campaigns
 * Format: 'Display Name=css-font-family-stack'
 *
 * Email-safe fonts only - web fonts removed due to inconsistent email client support
 * All fonts include fallbacks to widely supported system fonts
 * Organized by: Generic fonts, Modern system fonts, Core email-safe fonts
 */
const TINYMCE_FONT_FORMATS = [
	// Generic fonts (always available)
	'Sans Serif=sans-serif',
	'Serif=serif',

	// Modern Microsoft fonts (limited email client support)
	'Aptos=aptos,calibri,arial,helvetica,sans-serif',

	// Core email-safe system fonts
	'Arial=arial,helvetica,sans-serif',
	'Arial Black=arial black,avant garde,arial,helvetica,sans-serif',
	'Book Antiqua=book antiqua,palatino,georgia,times new roman,serif',
	'Calibri=calibri,arial,helvetica,sans-serif',
	'Century Gothic=century gothic,gothic,arial,helvetica,sans-serif',
	'Comic Sans MS=comic sans ms,trebuchet ms,arial,helvetica,sans-serif',
	'Courier New=courier new,courier,consolas,monaco,monospace',
	'Georgia=georgia,times new roman,times,serif',
	'Helvetica=helvetica,arial,sans-serif',
	'Tahoma=tahoma,arial,helvetica,sans-serif',
	'Times New Roman=times new roman,times,georgia,serif',
	'Trebuchet MS=trebuchet ms,arial,helvetica,sans-serif',
	'Verdana=verdana,arial,helvetica,sans-serif',
].join('; ');

interface IWordCountProvider {
	getCharacterCount(): number;
	getCharacterCountWithoutSpaces(): number;
	getWordCount(): number;
}

export interface IWordCountPlugin {
	body: IWordCountProvider;
	selection: IWordCountProvider;
}

export interface IRichContentDocumentEditorConfig {
	autoresizeContentBottomPadding?: number;
	autoresizeToFitContent?: boolean;
	contentHorizontalPadding?: number;
	contentRawCss?: string;
	defaultBlockStyleAttributeStringValue?: string;
	imageOptions?: IRichContentDocumentEditorImageOptions;
	maxHeight?: number;
	minHeight?: number;
	/**
	 * this overrides the useDefaultEmailPlaceholders, if provided
	 */
	placeholders?: IRichContentDocumentEditorPlaceholder[];
	plugins?: string | string[];
	/**
	 * Resource selector context for determining which placeholders to show
	 */
	resourceSelector?: Api.ResourceSelectorId;
	/**
	 * When true, skips applying default email style font to prevent UI issues in settings
	 */
	skipDefaultEmailStyleFont?: boolean;
	toolbar?: boolean | string | string[];
	toolbarOverflow?: 'floating' | 'sliding';
	useDefaultEmailPlaceholders?: boolean;
	validStyles?: string | Record<string, string>;
}

export interface IRichContentDocumentEditorProps
	extends IUserSessionComponentProps,
		IImpersonationContextComponentProps {
	autoFocus?: boolean;
	className?: string;
	collapsible?: boolean;
	config?: IRichContentDocumentEditorConfig;
	contentClassName?: string;
	contentPlaceholderText?: string;
	contentState: IRichContentEditorState;
	contentStyles?: StyleDeclarationValue[];
	disableFileAttachment?: boolean;
	disablePasteRichText?: boolean;
	loadingPlaceholder?: React.ReactNode;
	onBlur?(
		e: tinymce.EditorEvent<{
			focusedEditor: tinymce.Editor;
		}>
	): void;
	onContentStateChanged?(content: IRichContentEditorState): void;
	onExecuteCommand?(
		e: React.ChangeEvent<any> & {
			command: string;
			type: string;
			ui?: any;
			value?: any;
		}
	): void;
	onFocus?(
		e: tinymce.EditorEvent<{
			blurredEditor: tinymce.Editor;
		}>
	): void;
	onLoad?(editor: tinymce.Editor, contentState: IRichContentEditorState): void;
	readOnly?: boolean;
	readonlyChildren?: React.ReactNode;
	readOnlyUseFullEditor?: boolean;
	showAsPlainTextPreview?: boolean;
	showFirstNameTokenByDefault?: boolean;
	styles?: StyleDeclarationValue[];
}

export const DefaultRichContentEditorToolbarConfig = [ToolbarSignature];

export const DefaultRichContentEditorPlugins = ['lists', 'link', 'paste', 'wordcount'];

export interface IRichContentDocumentEditorError {
	error?: IOperationResultNoValue;
	source?: string;
}

interface IHtmlEditorSettings {
	autoresize_bottom_margin?: number;
	autoresize_overflow_padding?: number;
	content_style?: string;
	font_formats?: string;
	forced_root_block_attrs?: { style?: string };
	max_height?: number;
	max_width?: number;
	min_height?: number;
	min_width?: number;
	placeholders?: IRichContentDocumentEditorPlaceholder[];
	plugins?: string | string[];
	skipDefaultEmailStyleFont?: boolean;
	toolbar_drawer?: 'floating' | 'sliding';
	toolbar?: boolean | string | string[];
	valid_styles?: string | Record<string, string>;
}

const getContentAttributes = (editorRef: tinymce.Editor): IRichContentEditorStateAttributes => {
	const counter = editorRef?.plugins?.wordcount;
	if (counter) {
		return {
			document: {
				characterCount: counter.body.getCharacterCount(),
				wordCount: counter.body.getWordCount(),
			},
			selection: {
				characterCount: counter.selection.getCharacterCount(),
				wordCount: counter.selection.getWordCount(),
			},
		};
	}
	return {};
};

const getCurrentContentState = (
	contentHtmlStringValue: string,
	editorRef: tinymce.Editor,
	clearContentCallback?: () => void
) => {
	const content = createContentStateWithHtmlStringValue(contentHtmlStringValue || '', clearContentCallback);
	if (editorRef) {
		const plainTextContent = editorRef.getContent({
			format: 'text',
		}) as string;
		content.getPlainTextPreview = (truncate = true, terminator?: string) => {
			if (!truncate) {
				return `${plainTextContent || ''}${!!plainTextContent && !!terminator ? terminator : ''}`;
			}
			return `${(plainTextContent || '').substring(0, 100)}${!!plainTextContent && !!terminator ? terminator : ''}`;
		};

		const attributes = getContentAttributes(editorRef);
		if (attributes) {
			content.attributes = attributes;
		}
	}
	return content;
};

const resizeIE11Toolbar = (rootElement: HTMLDivElement) => {
	if (rootElement) {
		const toolbar =
			rootElement.querySelector('.tox.tox-tinymce .tox-editor-container .tox-editor-header') ||
			rootElement.querySelector('.tox.tox-tinymce .tox-editor-container .tox-editor-header');
		const targetWidth = rootElement.clientWidth - 2;
		if (!!toolbar && toolbar.clientWidth !== targetWidth) {
			toolbar.setAttribute('style', `width: ${targetWidth}px`);
		}
	}
};

function RichContentDocumentEditorBase(props: IRichContentDocumentEditorProps) {
	const {
		readOnly,
		className,
		styles,
		contentState,
		showAsPlainTextPreview,
		showFirstNameTokenByDefault,
		disableFileAttachment = false,
		readOnlyUseFullEditor,
		contentStyles,
		readonlyChildren,
		contentClassName,
		onContentStateChanged,
		autoFocus,
		contentPlaceholderText,
		disablePasteRichText = false,
		config,
		onLoad: onLoadProp,
		impersonationContext,
		onFocus,
		onBlur,
		userSession,
	} = props;
	const contentHtmlStringValueRef = React.useRef<string>();
	const contentHtmlStringValue = React.useMemo(() => {
		let value = contentState
			? (showAsPlainTextPreview
					? contentState.getPlainTextPreview(true, '...')
					: getContentHtmlStringValueFromRawRichTextContentState(contentState.getRawRichTextContent())) || ''
			: '';
		if (!contentHtmlStringValueRef.current && !value && showFirstNameTokenByDefault) {
			const token = new Token(FirstNamePlaceholder);
			value = `Hi ${token.toHTMLString()},`;
		}
		return value;
	}, [contentState, showAsPlainTextPreview, showFirstNameTokenByDefault]);

	const [loadingEditor, setLoadingEditor] = React.useState<boolean>(true);
	const [showGiphyChooser, setShowGiphyChooser] = React.useState<boolean>(false);
	const [showYouTubeChooser, setShowYouTubeChooser] = React.useState<boolean>(false);
	const [showPixabayChooser, setShowPixabayChooser] = React.useState<boolean>(false);

	// UnifiedImageDialog state
	const [showUnifiedImageDialog, setShowUnifiedImageDialog] = React.useState<boolean>(false);
	const [unifiedImageDialogTab, setUnifiedImageDialogTab] = React.useState<
		'upload' | 'ai-generate' | 'ai-edit' | 'free-images'
	>('upload');
	const [editingImage, setEditingImage] = React.useState<IFileAttachment | null>(null);
	const [imageEditMode, setImageEditMode] = React.useState<'insert' | 'edit'>('insert');

	const editorRef = React.useRef<tinymce.Editor>();
	const rootElementRef = React.useRef<HTMLDivElement>(null);
	const selectedImageElementRef = React.useRef<HTMLImageElement | null>(null);

	const account = impersonationContext?.account || userSession.account;
	const hasCustomContactFields = account?.additionalInfo?.hasCustomContactFields;
	const customFieldsQuery = useContactCustomFields({
		impersonationContext,
		refetchOnWindowFocus: false,
		enabled: hasCustomContactFields,
	});

	const integrationFieldsQuery = useContactIntegrationCustomFieldsQuery({
		impersonationContext,
		refetchOnWindowFocus: false,
		enabled: true, // Always enabled - can be controlled by feature flag if needed
	});

	const editorConfig = React.useMemo(() => {
		const settings: IHtmlEditorSettings = {
			autoresize_bottom_margin: 10,
			// @see https://www.tiny.cloud/docs/configure/editor-appearance/#font_formats
			font_formats: TINYMCE_FONT_FORMATS,
			plugins: [...DefaultRichContentEditorPlugins],
			toolbar: [...DefaultRichContentEditorToolbarConfig],
		};

		// handle initial config
		if (config) {
			if (config.plugins) {
				if (typeof config.plugins === 'string') {
					(settings.plugins as string[]).push(config.plugins as string);
				} else {
					settings.plugins = [...(settings.plugins as string[]), ...(config.plugins as string[])];
				}
			}

			if (config.placeholders) {
				settings.placeholders = config.placeholders;
			} else if (config.useDefaultEmailPlaceholders) {
				settings.placeholders = [
					...GetDefaultEmailPlaceholders(userSession, impersonationContext, config.resourceSelector),
					...PlaceholdersPlugin.getCustomFieldPlaceholders(customFieldsQuery.data?.fields),
					...(integrationFieldsQuery?.data ?? []),
				];
			}
			if (config.toolbar !== undefined && config.toolbar !== null) {
				settings.toolbar = config.toolbar;
			}

			if (config.contentRawCss) {
				settings.content_style = config.contentRawCss;
			}

			if (config.defaultBlockStyleAttributeStringValue) {
				settings.forced_root_block_attrs = {
					style: config.defaultBlockStyleAttributeStringValue,
				};
			}

			if (config.autoresizeToFitContent) {
				const plugins = settings.plugins
					? typeof settings.plugins === 'string'
						? settings.plugins || ''
						: (settings.plugins || []).join(' ')
					: '';
				settings.plugins = `${plugins.trim()} autoresize noneditable importcss`;
			}

			if (config.minHeight !== undefined && config.minHeight !== null) {
				settings.min_height = config.minHeight;
			}

			if (config.maxHeight !== undefined && config.maxHeight !== null) {
				settings.max_height = config.maxHeight;
			}

			if (config.contentHorizontalPadding !== undefined && config.contentHorizontalPadding !== null) {
				settings.autoresize_overflow_padding = config.contentHorizontalPadding;
			}

			if (config.autoresizeContentBottomPadding !== undefined && config.autoresizeContentBottomPadding !== null) {
				settings.autoresize_bottom_margin = config.autoresizeContentBottomPadding;
			}

			if (config.toolbarOverflow !== undefined && config.toolbarOverflow !== null) {
				settings.toolbar_drawer = config.toolbarOverflow;
			}

			if (config.contentRawCss) {
				settings.content_style = config.contentRawCss;
			}

			if (config.validStyles) {
				settings.valid_styles = config.validStyles;
			}

			if (config.skipDefaultEmailStyleFont) {
				settings.skipDefaultEmailStyleFont = config.skipDefaultEmailStyleFont;
			}
		}
		return settings;
	}, [config, customFieldsQuery.data?.fields, integrationFieldsQuery.data, impersonationContext, userSession]);

	const onReadOnlyContentMouseOver = (e: React.MouseEvent<HTMLElement>) => {
		const el = e?.target as HTMLElement;
		if (!!config?.placeholders && el.getAttribute('data-placeholder') === 'true') {
			const dataValue = el.getAttribute('data-placeholder-value');
			const placeholder = config.placeholders?.find(x => x.value === dataValue);
			if (placeholder?.onClickCommand) {
				onExecuteCommand?.({
					...e,
					command: placeholder.onClickCommand,
					value: { sourceEvent: e, placeholderValue: placeholder.value },
				});
			}
		}
	};

	const onReadOnlyContentMouseOut = (e: React.MouseEvent<HTMLElement>) => {
		const el = e?.target as HTMLElement;
		if (!!config?.placeholders && el.getAttribute('data-placeholder') === 'true') {
			const dataValue = el.getAttribute('data-placeholder-value');
			const placeholder = config.placeholders?.find(x => x.value === dataValue);
			if (placeholder?.onCloseCommand) {
				onExecuteCommand?.({
					...e,
					command: placeholder.onCloseCommand,
					value: { sourceEvent: e },
				});
			}
		}
	};

	const onEditorChangedRef = React.useRef<(htmlStringValue: string, editorRef?: tinymce.Editor) => void>();
	const clearContent = React.useCallback(() => {
		onEditorChangedRef.current?.('', null);
	}, []);
	const onEditorChanged = React.useCallback(
		(htmlStringValue: string, editor?: tinymce.Editor) => {
			if (htmlStringValue !== contentHtmlStringValueRef.current && onContentStateChanged) {
				const content = getCurrentContentState(htmlStringValue, editor, clearContent);
				onContentStateChanged(content);
			}
		},
		[clearContent, onContentStateChanged]
	);

	const onExecuteCommand = (
		e: React.ChangeEvent<any> & {
			command: string;
			type: string;
			ui?: any;
			value?: any;
		}
	) => {
		const { onExecuteCommand: onExecuteCommandProps } = props;
		onExecuteCommandProps?.(e);

		if (!e.defaultPrevented && e.command === levInsertGiphyCommand) {
			setShowGiphyChooser(true);
		} else if (!e.defaultPrevented && e.command === levShowYouTubeChooserCommand) {
			setShowYouTubeChooser(true);
		} else if (!e.defaultPrevented && e.command === levShowImageChooserCommand) {
			// Insert mode - open dialog with upload tab
			setEditingImage(null);
			setUnifiedImageDialogTab('upload');
			setShowUnifiedImageDialog(true);
		} else if (!e.defaultPrevented && e.command === levEditImageCommand) {
			// Edit mode - convert selected image to FileAttachment and open AI Edit tab
			if (selectedImageElementRef.current) {
				const imgElement = selectedImageElementRef.current;

				// Convert image to FileAttachment asynchronously
				convertImageElementToFileAttachment(imgElement, userSession, impersonationContext)
					.then(attachment => {
						setEditingImage(attachment);
						setImageEditMode('edit');
						setUnifiedImageDialogTab('ai-edit');
						setShowUnifiedImageDialog(true);
					})
					.catch(error => {
						editorRef.current?.execCommand('levError', true, {
							error: asApiError(error),
							source: 'edit-image',
						});
					});
			}
		} else if (!e.defaultPrevented && e.command === levShowPixabayChooserCommand) {
			setShowPixabayChooser(true);
		}
	};

	const onGiphyChooserRequestClose = (gif?: IGif, cancel?: boolean) => {
		setShowGiphyChooser(false);
		if (!!gif && !cancel && !!editorRef.current) {
			// try to constrain the width to something sane
			const width = Math.min(
				gif.images.original.width,
				rootElementRef.current ? rootElementRef.current.clientWidth - 40 : 300
			);
			editorRef.current.insertContent(
				`<img title="${gif.title || ''}" src="${gif.images.original.url}" width="${width}px"/>`
			);
			onEditorChanged(editorRef.current.getContent() as string, editorRef.current);
		}
	};

	const onYouTubeChooserRequestClose = (videoInfo?: IYouTubeVideoInfo, cancel?: boolean) => {
		setShowYouTubeChooser(false);
		if (!!videoInfo && !cancel && !!editorRef.current) {
			// Check if this is a blog editor by looking at toolbar configuration
			const isBlogEditor = config?.toolbar?.toString().includes('h1 h2 h3');
			const editorType = isBlogEditor ? 'blog' : 'email';
			const thumbnailHtml = YouTubeUtils.generateThumbnailHtml(videoInfo, undefined, editorType);
			editorRef.current.insertContent(thumbnailHtml);
			onEditorChanged(editorRef.current.getContent() as string, editorRef.current);
		}
	};

	const onPixabayChooserRequestClose = (_result?: any, _cancel?: boolean) => {
		setShowPixabayChooser(false);
		// Note: MediaChooser uses onSave callback instead of onRequestClose result
		// The actual image insertion is handled by onPixabayImageSave
	};

	const onPixabayImageSave = (fileAttachment: IFileAttachment, altText?: string) => {
		if (!!fileAttachment && !!editorRef.current) {
			// Create image HTML using the uploaded file attachment
			const width = Math.min(
				600, // max width for emails
				rootElementRef.current ? rootElementRef.current.clientWidth - 40 : 300
			);

			const altAttribute = altText ? ` alt="${altText}"` : '';
			const imgHtml = `<img src="${fileAttachment.url}" data-id="${uuid()}" width="${width}px" style="max-width: 100%; height: auto;"${altAttribute} />`;
			editorRef.current.insertContent(imgHtml);
			onEditorChanged(editorRef.current.getContent() as string, editorRef.current);
		}
		// Close the modal after successful image insertion
		setShowPixabayChooser(false);
	};

	/**
	 * Extracts plain text content from the editor for AI-powered image suggestions.
	 * Used by UnifiedImageDialog's Free Images tab to generate relevant image keywords.
	 *
	 * @returns Plain text content (no HTML tags), trimmed of whitespace
	 */
	const getEmailContentForAI = React.useCallback((): string => {
		if (!editorRef.current) return '';
		// Get plain text content from editor (not HTML) for cleaner AI processing
		const content = editorRef.current.getContent({ format: 'text' });
		return content.trim();
	}, []);

	// Handler for UnifiedImageDialog close/image selection
	const onUnifiedImageDialogClose = React.useCallback(
		async (selectedImage: IFileAttachment | null) => {
			setShowUnifiedImageDialog(false);

			if (!selectedImage || !editorRef.current) {
				// User cancelled or no image selected
				setEditingImage(null);
				setImageEditMode('insert');
				return;
			}

			try {
				const isEditMode = imageEditMode === 'edit';

				// Validate file size (similar to existing patterns)
				const maxSize = EmailMessageViewModel.MaxByteCount;
				if (selectedImage.fileSize > maxSize) {
					throw new Error(`Image size exceeds maximum allowed size of ${maxSize} bytes`);
				}

				// Load image to get dimensions
				const img = new Image();
				await new Promise<void>((resolve, reject) => {
					img.onload = () => resolve();
					img.onerror = () => reject(new Error('Failed to load image'));
					img.src = selectedImage.url || ''; // Empty string will trigger onerror
				});

				// Calculate scaled dimensions (max width 600px for emails)
				const maxWidth = 600;
				const maxHeight = 400;

				// Use single scale factor to preserve aspect ratio
				const scale = Math.min(1, maxWidth / img.width, maxHeight / img.height);
				const width = Math.round(img.width * scale);
				const height = Math.round(img.height * scale);

				// Build image attributes
				const imgAttribs: Record<string, string | number> = {
					src: selectedImage.url || '',
					width,
					height,
					style: 'max-width: 100%; height: auto;',
				};

				// Add data-id for file tracking
				if (selectedImage.id) {
					imgAttribs['data-id'] = selectedImage.id;
				}

				// Add alt text from fileName if available
				if (selectedImage.fileName) {
					imgAttribs.alt = selectedImage.fileName.replace(/\.[^/.]+$/, ''); // Remove file extension
				}

				if (isEditMode && selectedImageElementRef.current) {
					// Edit mode - update existing image attributes using TinyMCE's DOM API
					// Re-select the node to ensure the reference is fresh
					editorRef.current.selection.select(selectedImageElementRef.current);

					// Use TinyMCE's DOM API to set attributes (ensures TinyMCE tracks the changes)
					editorRef.current.dom.setAttribs(selectedImageElementRef.current, imgAttribs);

					// Force TinyMCE to detect the change
					editorRef.current.nodeChanged();

					onEditorChanged(editorRef.current.getContent() as string, editorRef.current);
				} else {
					// Insert mode - add new image
					const imgHtml = `<img ${Object.entries(imgAttribs)
						.map(([key, value]) => `${key}="${value}"`)
						.join(' ')} />`;

					editorRef.current.insertContent(imgHtml);
					onEditorChanged(editorRef.current.getContent() as string, editorRef.current);
				}

				// Clear editing state
				setEditingImage(null);
				setImageEditMode('insert');
			} catch (error) {
				const apiError = asApiError(error);
				editorRef.current?.execCommand('levError', true, {
					error: apiError,
					source: imageEditMode === 'edit' ? 'edit-image' : 'insert-image',
				});
				setEditingImage(null);
				setImageEditMode('insert');
			}
		},
		[imageEditMode, onEditorChanged]
	);

	const onLoad = (editor: tinymce.Editor) => {
		editorRef.current = editor;
		if (editor.getContent({ format: 'text' }).length !== contentState?.getPlainTextPreview(false)?.length) {
			onEditorChanged(editorRef.current.getContent() as string, editorRef.current);
		}
		setLoadingEditor(false);
	};

	React.useEffect(() => {
		const handler = () => {
			resizeIE11Toolbar(rootElementRef.current);
		};
		if (isIE11()) {
			window.addEventListener('resize', handler);
		}
		return () => {
			if (isIE11()) {
				window.removeEventListener('resize', handler);
			}
		};
	}, []);

	React.useEffect(() => {
		editorRef.current?.execCommand(disableFileAttachment ? disableFileAttachmentCommand : enableFileAttachmentCommand);
	}, [disableFileAttachment]);

	React.useEffect(() => {
		if (!loadingEditor) {
			if (isIE11()) {
				resizeIE11Toolbar(rootElementRef.current);
			}
			if (onLoadProp) {
				requestAnimationFrame(() => {
					onLoadProp(
						editorRef.current,
						getCurrentContentState(contentHtmlStringValueRef.current, editorRef.current, clearContent)
					);
				});
			}
		}
	}, [clearContent, loadingEditor, onLoadProp]);

	React.useEffect(() => {
		onEditorChangedRef.current = onEditorChanged;
		contentHtmlStringValueRef.current = contentHtmlStringValue;
	});

	// Track selected images for edit functionality
	React.useEffect(() => {
		if (!editorRef.current) return;

		const editor = editorRef.current;
		const handleNodeChange = (e: any) => {
			const selectedNode = e.element;
			selectedImageElementRef.current =
				selectedNode && selectedNode.nodeName === 'IMG' ? (selectedNode as HTMLImageElement) : null;
		};

		editor.on('NodeChange', handleNodeChange);

		return () => {
			editor.off('NodeChange', handleNodeChange);
		};
	}, [loadingEditor]); // Run after editor loads

	const renderLoading = () => {
		const { loadingPlaceholder } = props;
		if (loadingPlaceholder) {
			return loadingPlaceholder;
		}

		return <LoadingSpinner className='absolute-center' type='small' />;
	};

	return (
		<div
			className={`${css(styleSheet.container, ...(styles || []))} rich-content-document-editor ${className || ''}`}
			ref={!readOnly ? rootElementRef : undefined}
		>
			{readOnly && !readOnlyUseFullEditor ? (
				<>
					<div
						className={`${css(
							styleSheet.readOnlyContent,
							...(contentStyles || [])
						)} rich-content-document-editor-readonly ${contentClassName || ''}`}
						dangerouslySetInnerHTML={{ __html: contentHtmlStringValue }}
						onMouseOver={onReadOnlyContentMouseOver}
						onMouseOut={onReadOnlyContentMouseOut}
					/>
					{readonlyChildren}
				</>
			) : (
				<>
					{customFieldsQuery.isPending && hasCustomContactFields ? null : (
						<React.Suspense fallback={null}>
							<LazyHtmlEditor
								autoFocus={autoFocus}
								contentPlaceholderText={contentPlaceholderText}
								disablePasteRichText={disablePasteRichText}
								imageOptions={config?.imageOptions}
								init={editorConfig}
								onBlur={onBlur}
								onEditorChanged={onEditorChanged}
								onExecuteCommand={onExecuteCommand}
								onFocus={onFocus}
								onLoad={onLoad}
								readOnly={readOnly}
								userSession={userSession}
								value={contentHtmlStringValue || ''}
							/>
						</React.Suspense>
					)}

					{loadingEditor ? (
						renderLoading()
					) : (
						<>
							<GiphyChooserModal
								modalProps={{
									isOpen: showGiphyChooser,
									onRequestClose: onGiphyChooserRequestClose,
								}}
							/>
							<YouTubeChooserModal
								modalProps={{
									isOpen: showYouTubeChooser,
									onRequestClose: onYouTubeChooserRequestClose,
								}}
							/>
							<UnifiedImageDialog
								open={showUnifiedImageDialog}
								onOpenChange={setShowUnifiedImageDialog}
								initialTab={unifiedImageDialogTab}
								initialImage={editingImage}
								postContent={getEmailContentForAI()}
								impersonationContext={impersonationContext}
								onImageSelected={onUnifiedImageDialogClose}
							/>
							<MediaChooserModal
								content={contentHtmlStringValue}
								imageOnly={true}
								onSaveWithAltText={onPixabayImageSave}
								showAltTextInput={true}
								modalProps={{
									isOpen: showPixabayChooser,
									onRequestClose: onPixabayChooserRequestClose,
								}}
							/>
						</>
					)}
				</>
			)}
		</div>
	);
}

export const RichContentDocumentEditor = inject(
	UserSessionViewModelKey,
	ImpersonationContextKey
)(RichContentDocumentEditorBase);
